/*  EDR Configuration Types */
export type EDRConfigurationType =
  | 'crowdstrike'
  | 'microsoft_defender'
  | 'palo_alto_networks'
  | 'microsoft_entra_id';

export interface EDRConfiguration {
  provider_name: EDRConfigurationType;
  enabled: boolean;
  polling_seconds: number;
  edr_action_enabled: boolean;
  tenant: string;
}

export interface CrowdStrikeConfiguration extends EDRConfiguration {
  provider_name: 'crowdstrike';
  base_url: string;
  client_id: string;
  client_secret: string;
}

export interface MicrosoftDefenderConfiguration extends EDRConfiguration {
  provider_name: 'microsoft_defender';
  app_id: string;
  app_secret: string;
  defender_tenant_id: string;
}

export interface PaloAltoConfiguration extends EDRConfiguration {
  provider_name: 'palo_alto_networks';
  access_key: string;
  bucket: string;
  prefix: string;
  secret_key: string;
}

export type VerifiedEDRConfiguration = {
  verified: boolean;
};

export interface CrowdStrikeConfigurationInput {
  client_id: string;
  client_secret: string;
  base_url: string;
  enabled: boolean;
  edr_action_enabled: boolean;
  polling_seconds: number;
}

export interface MicrosoftDefenderConfigurationInput {
  defender_tenant_id: string;
  app_id: string;
  app_secret: string;
  enabled: boolean;
  edr_action_enabled: boolean;
  polling_seconds: number;
}

export interface EDRConfigurationInput {
  provider_name: EDRConfigurationType;
  crowdstrike: CrowdStrikeConfigurationInput;
  microsoft_defender: MicrosoftDefenderConfigurationInput;
}

export interface IStoreEDRConfiguration {
  storeEDRConfiguration: EDRConfiguration;
}

export interface IDeleteEDRConfiguration {
  deleteEDRConfiguration: EDRConfiguration;
}

export interface GetEDRConfigurationRequest {
  provider_name: EDRConfigurationType;
  tenant?: string;
}

export interface GetEDRConfigurationsRequest {
  tenant?: string;
}

export type EDRConfigurationShape =
  | CrowdStrikeConfiguration
  | MicrosoftDefenderConfiguration
  | PaloAltoConfiguration;

export interface GetEDRConfigurationResponse {
  getEDRConfiguration: EDRConfigurationShape;
}

export interface GetEDRConfigurationsResponse {
  getEDRConfigurations: EDRConfigurationShape[];
}

export interface GetEDRConfigurationResponsePublic {
  getEDRConfiguration: EDRConfiguration;
}

export interface GetEDRConfigurationsResponsePublic {
  getEDRConfigurations: {
    edr_configurations: EDRConfiguration[];
    offset: number;
    size: number;
    total_items: number;
  };
}

export interface IVerifyEDRConfiguration {
  verifyEDRConfiguration: VerifiedEDRConfiguration;
}
/* End Configuration Types */

export enum EntityDataStoreAlertEntityType {
  DOMAIN,
  HOST,
  IP,
  IP_RANGE,
  MAC,
  USER,
}

export interface AlertEntityInfoInput {
  entity_name: string;
  entity_type: EntityDataStoreAlertEntityType;
}

export enum EntityDataStoreEntityType {
  ip,
  host,
  cert,
  domain,
  network_device,
  user,
  software,
  service,
  location,
  network,
  endpoint_device,
}

export enum AttributeValueType {
  str_type,
  int_type,
  float_type,
  bool_type,
  null_type,
}

export interface NICHistory {
  ip_address: string;
  mac_address: string;
  obsts: number;
  data_source: string;
  tenant: string;
}

export interface PaginatedNICHistory {
  as_of: number;
  offset: number;
  size: number;
  max_results: number;
  total_items: number;
  use_cache: boolean;
  nic_histories: NICHistory[];
}

export interface UserHistory {
  username: string;
  obsts: number;
  data_source: string;
  tenant: string;
}

export interface PaginatedUserHistory {
  as_of: number;
  offset: number;
  size: number;
  max_results: number;
  total_items: number;
  use_cache: boolean;
  user_histories: UserHistory[];
}

export interface EdrPayload {
  entity_status: string;
  entity_status_updated_timestamp: number;
  tenant: string;
  entity_type: EntityDataStoreEntityType;
  entity_id: string;
  device_id: string;
  data_source: string;
  obsts: number;
  expire_at: number;
  last_updated_timestamp: number;
  platform: string;
  platform_type: string;
  os: string;
  sensor_version: string;
  cloud_account_id: string;
  cloud_instance_id: string;
  cloud_provider: string;
  ad_domain_name: string;
  ad_organizational_unit_name: string;
  mac_addresses: string[];
  external_ips: string[];
  host_names: string[];
  nic_history: PaginatedNICHistory;
  user_history: PaginatedUserHistory;
}
export interface IGetEDRPayload {
  getEdrPayload: EdrPayload;
}

export interface EntityIdInput {
  tenant: string;
  entity_type: EntityDataStoreEntityType;
  entity_id: string;
}

export interface IGetNicHistoryForEndpointDeviceEntity {
  getNicHistoryForEndpointDeviceEntity: PaginatedNICHistory;
}

export interface IGetUserHistoryForEndpointDeviceEntity {
  getUserHistoryForEndpointDeviceEntity: PaginatedUserHistory;
}

export enum EntityEdrStatus {
  Normal = 'normal',
  IsolationPending = 'isolation_pending',
  IsolationRequested = 'isolation_requested',
  Isolated = 'isolated',
  LiftIsolationPending = 'lift_isolation_pending',
  LiftIsolationRequested = 'lift_isolation_requested',
  Blocked = 'blocked',
  AccountEnabled = 'account_enabled',
  AccountDisabled = 'account_disabled',
}

export interface EntityEdrStatusWithTimestamp {
  entity_edr_status: EntityEdrStatus;
  updated_timestamp: number;
  tenant: string;
}

export interface EntityEdrStatusWithTimestampExt {
  entity_edr_status: EntityEdrStatus;
  updated_timestamp: number;
  device_id: string;
  hostname: string;
  edr_provider: string;
  tenant: string;
}

export interface IPullEntityEdrStatus {
  pullEntityEdrStatus: EntityEdrStatusWithTimestamp;
}

export interface IGetEntityEdrStatus {
  getEntityEdrStatus: EntityEdrStatusWithTimestamp;
}

export interface IPerformEdrAction {
  performEdrAction: EntityEdrStatusWithTimestampExt;
}

export type EntityAction =
  | 'isolate'
  | 'lift_isolation'
  | 'universal_logout'
  | 'password_reset'
  | 'increase_risk_score'
  | 'disable_account'
  | 'enable_account';

export interface EdrDeviceInput {
  entity_id?: string;
  detection_id?: string;
}

export interface EdrEndpointDevice {
  entity_status: string;
  entity_status_updated_timestamp: number;
  tenant: string;
  entity_type: EntityDataStoreEntityType;
  entity_id: string;
  data_source: EDRConfigurationType;
  obsts: number;
  expire_at: number;
  last_updated_timestamp: number;
}

export interface CrowdStrikeHostInfo {
  platform: string;
  platform_type: string;
  os: string;
  sensor_version: string;
  cloud_account_id: string;
  cloud_instance_id: string;
  cloud_provider: string;
  ad_domain_name: string;
  ad_organizational_unit_name: string;
  mac_address: string;
  external_ip: string;
  host_name: string;
  local_ip: string;
}

export interface CrowdStrikeEndpointDevice extends EdrEndpointDevice {
  entity_status: string;
  entity_status_updated_timestamp: number;
  tenant: string;
  entity_type: EntityDataStoreEntityType;
  entity_id: string;
  data_source: EDRConfigurationType;
  obsts: number;
  expire_at: number;
  last_updated_timestamp: number;
  device_id: string;
  host_info: CrowdStrikeHostInfo;
  nic_history: PaginatedNICHistory;
  user_history: PaginatedUserHistory;
}

export interface MicrosoftDefenderMachineActions {
  id: string;
  type: string;
  status: string;
  requestor: string;
  requestorComment: string;
  creationDateTimeUtc: number;
  lastUpdateDateTimeUtc: number;
}

export interface MicrosoftDefenderVmMetadata {
  vmId: string;
  cloudProvider: string;
  resourceId: string;
  subscriptionId: string;
}

export interface MicrosoftDefenderIpAddresses {
  ipAddress: string;
  macAddress: string;
  type: string;
  operationalStatus: string;
}

export interface MicrosoftDefenderMachine {
  mergedIntoMachineId: string;
  isPotentialDuplication: boolean;
  isExcluded: boolean;
  exclusionReason: string;
  computerDnsName: string;
  firstSeen: number;
  lastSeen: number;
  osPlatform: string;
  osVersion: string;
  osProcessor: string;
  version: string;
  lastIpAddress: string;
  lastExternalIpAddress: string;
  agentVersion: string;
  osBuild: number;
  healthStatus: string;
  deviceValue: string;
  rbacGroupId: number;
  rbacGroupName: string;
  riskScore: string;
  exposureLevel: string;
  isAadJoined: boolean;
  aadDeviceId: string;
  machineTags: string[];
  defenderAvStatus: string;
  onboardingStatus: string;
  osArchitecture: string;
  managedBy: string;
  managedByStatus: string;
  ipAddresses: MicrosoftDefenderIpAddresses[];
  vmMetadata: MicrosoftDefenderVmMetadata;
}

export interface MicrosoftDefenderEndpointDevice extends EdrEndpointDevice {
  entity_status: string;
  entity_status_updated_timestamp: number;
  tenant: string;
  entity_type: EntityDataStoreEntityType;
  entity_id: string;
  data_source: EDRConfigurationType;
  obsts: number;
  expire_at: number;
  last_updated_timestamp: number;
  id: string;
  machine: MicrosoftDefenderMachine;
  machine_actions: MicrosoftDefenderMachineActions[];
}

export interface MicrosoftEntraMachine {
  lastIpAddress: string;
  local_ip: string;
  ip_type: 'local';

  computerDnsName: string;
  aadDeviceId: string;

  osPlatform: string;
  osVersion: string;

  machineTags: string[];
  lastSeen: number;
  source: 'defender_api';
}

export interface EntraLocation {
  city: string;
  state: string;
  country_or_region: string;
}

export interface EntraSigninStatus {
  errorCode: number;
  failureReason: string;
  additionalDetails: string;
}

export interface EntraDeviceDetail {
  device_id: string;
  display_name: string;
  operating_system: string;
  is_compliant: boolean;
  is_managed: boolean;
}

export interface MicrosoftEntraLastSignIn {
  event_id?: string;
  created_date_time: string;
  created_date_time_epoch: number;

  external_ip: string;
  application_display_name?: string;
  client_app_used: string;
  conditional_access_status?: string;

  risk_level_aggregated: string;
  risk_level_during_signin?: string;

  status?: EntraSigninStatus;
  location?: EntraLocation;
  device_detail?: EntraDeviceDetail;

  // Legacy fields kept for compatibility with tests and old data
  ip_type?: string;
  city?: string;
  state?: string;
  country_or_region?: string;
  app_display_name?: string;
  risk_state?: string;

  device_id?: string;
  device_display_name?: string;
  operating_system?: string;
  is_compliant?: boolean;
  is_managed?: boolean;
}

// Entra-specific fields (manual types)
export interface MicrosoftEntraUserInfo {
  id: string;
  user_principal_name: string;
  display_name: string;
  given_name: string;
  surname: string;
  mail: string;
  job_title: string;
  department: string;
  office_location: string;
  business_phones: string[];
  mobile_phone: string;
  account_enabled: boolean;
}

export type EdrEndpointDevices =
  | MicrosoftDefenderEndpointDevice
  | CrowdStrikeEndpointDevice;

export interface PaginatedEdrEndpointDevices {
  edr_endpoint_devices: EdrEndpointDevices[];
  offset: number;
  size: number;
  total_items: number;
}

export interface IGetEDREndpointDevices {
  getEdrEndpointDevices: PaginatedEdrEndpointDevices;
}

export interface EdrEndpointDeviceInput {
  alert_entity_info: AlertEntityInfoInput;
  as_of: number;
  tenant: string;
}

// Manual types based on JIRA ticket description
export interface EntityComment {
  comment_id: string;
  comment: string;
  author_alias: string;
  created_at: number;
  severity?: number;
  alert_name?: string;
  // entity_ip is not available directly on Comment type
  // May need to fetch from detection if parent is a detection
  parent?: {
    parent_object_id?: string;
    parent_object_type?: string;
  };
}

export interface GetCommentsByEntityNamePaginatedResponse {
  getCommentsByEntityNamePaginated: {
    total_count: number;
    offset: number;
    size: number;
    comments: EntityComment[];
  };
}

export interface GetCommentsByEntityNamePaginatedVariables {
  entity_name: string;
  tenant: string;
  offset?: number;
  size?: number;
  sort?: { field: string; direction: 'asc' | 'desc' };
  start_ts: number;
  end_ts: number;
}

export interface EntraSigninHistorySearchInput {
  tenant: string;
  device_id?: string;
  display_name?: string;
  offset: number;
  size: number;
  as_of: number;
}

export interface EntraSigninWithUser {
  signin: {
    tenant: string;
    timestamp: number;
    microsoft_entra_id: {
      user_id: string;
      user_principal_name: string;
      sign_in: MicrosoftEntraLastSignIn;
      metadata: {
        last_refreshed_at: number;
        source: string;
      };
      raw: string;
    };
  };
  user: {
    tenant: string;
    timestamp: number;
    microsoft_entra_id: MicrosoftEntraUserInfo & {
      risk_level: string;
      ADGroup: string[];
      raw: string;
    };
  };
}
